// LinkOverlay.jsx · the link-refresh overlay (step 2, October 2026), shared by FairPlayPlayers.jsx (Safari, Video.js)
// and WidevinePlayers.jsx (Chrome and Safari desktop, shaka). Moved here unchanged from FairPlayPlayers.jsx on
// October 15, 2026 so both players show exactly the same thing: "Welcome back · Reloading your lesson…" while a
// fresh link loads in the background, then "Resume lesson · Picks up at m:ss"; that tap calls play() inside the
// gesture, so the video starts with sound.
import React, { useEffect, useState } from 'react';

// Resume at tap (October 20, 2026): while the tap refreshes the session, the link and the video ('resuming'), the
// caption rotates through these so the wait reads as progress; 'loading' (a reload the player started itself) keeps
// its one line.
const RESUMING_CAPTIONS = ['Refreshing your session…', 'Getting a fresh link…', 'Reloading your lesson…', 'Almost there…'];
const RESUMING_CAPTION_MS = 1600;

export function formatSpot(s) {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
}
export function LinkOverlay({ state, at, colors, onContinue, small }) {
  const [captionIndex, setCaptionIndex] = useState(0);
  useEffect(() => {
    if (state !== 'resuming') return undefined;
    setCaptionIndex(0);
    const timer = setInterval(() => setCaptionIndex((i) => (i + 1) % RESUMING_CAPTIONS.length), RESUMING_CAPTION_MS);
    return () => clearInterval(timer);
  }, [state]);
  if (!state) return null;
  const accent = (colors && colors.accent) || '#C9A962';
  const font = '"DM Sans",sans-serif';
  return (
    <div data-link-overlay={state} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: small ? 10 : 14, padding: 24, textAlign: 'center', background: 'rgba(0,0,0,0.95)', zIndex: 30 }}>
      {state === 'loading' || state === 'resuming' ? (<>
        <div style={{ width: small ? 32 : 40, height: small ? 32 : 40, border: '3px solid rgba(255,255,255,0.18)', borderTopColor: accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ margin: 0, color: '#fff', fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: small ? 20 : 26, fontWeight: 400, lineHeight: 1.2 }}>Welcome back</p>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontFamily: font, fontSize: small ? 12 : 14 }}>{state === 'resuming' ? RESUMING_CAPTIONS[captionIndex] : 'Reloading your lesson…'}</p>
      </>) : (<>
        <p style={{ margin: 0, color: '#fff', fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: small ? 20 : 26, fontWeight: 400, lineHeight: 1.2 }}>Welcome back</p>
        <button type="button" onClick={onContinue} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minHeight: small ? 44 : 50, padding: small ? '0 22px 0 18px' : '0 28px 0 22px', borderRadius: 999, border: 'none', background: accent, color: '#1A1A1A', fontFamily: font, fontSize: small ? 14 : 15, fontWeight: 600, letterSpacing: '0.02em', cursor: 'pointer', boxShadow: '0 8px 28px rgba(0,0,0,0.45)' }}>
          <svg width={small ? 16 : 18} height={small ? 16 : 18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.5-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5z" /></svg>
          Resume lesson
        </button>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontFamily: font, fontSize: small ? 12 : 13 }}>Picks up at {formatSpot(at)}</p>
      </>)}
    </div>
  );
}
